import Vue from "vue";
import Router from "vue-router";
import store from "@/store";

import Home from "@/views/Home";
import SurveyContainer from "@/views/SurveyContainer";
import Login from "@/views/UserViews/Login";
import Dashboard from "@/views/Dashboard";
import Charts from "@/views/Charts";
import StakeholderList from "@/views/StakeholderList";
import Map from "@/views/Map";
import PasswordReset from "@/views/UserViews/PasswordReset";
import PasswordResetConfirm from "@/views/UserViews/PasswordResetConfirm";
import Calendar from "@/views/Calendar";
import SurveyResponses from "@/views/SurveyResponses";
import Conversation from "@/views/Conversation/Conversation";
import MessageRoom from "@/components/Conversation/MessageRoom";
import EmailVerification from "@/views/UserViews/EmailVerification";
import Error from "@/views/Utilities/ErrorView";
import Exhibits from "@/views/Exhibits"
import AdminSurveys from "@/views/AdminSurveys.vue"
import PreviewSurveys from "@/components/Admin/PreviewSurveys.vue"
import AdminMainContainer from "@/views/AdminMainContainer"
import StoryMap from "@/views/StoryMap"

Vue.use(Router);

const requireAuthenticated = (to, from, next) => {

  store.dispatch("auth/initialize").then(() => {
    if (!store.getters["auth/isAuthenticated"]) {
      if (to.name === "messageroom") {
        next()
      } else {
        next("/login");
      }
    } else {
      next();
    }
  });
};

const requireUnauthenticated = (to, from, next) => {
  store.dispatch("auth/initialize").then(() => {
    if (store.getters["auth/isAuthenticated"]) {
      next("/");
    } else {
      next();
    }
  });
};

const requireLoggedIn = (to, from, next) => {
  if (
    !store.state.stakeholders.contactID ||
    !store.state.stakeholders.currentStakeholderID
  ) {
    alert("You can't take the survey anonymously.");
    store.dispatch("programs/getPrograms").then(() => {
      store.dispatch("projects/getProjects").then(() => {
        store.dispatch("survey/fetchSurveys").then(() => {
          isValid(to);
          if (program.view_multiple_surveys) {
            if (survey.view_in_list) {
              next({ path: to.params.program_name + "/", params: { program_name: to.params.program_name } });
            } else {
              next({ path: to.params.program_name + "/" + to.params.project_name + "/" + to.params.survey_name, params: to.params });
            }
          } else {
            next({ path: to.params.program_name + "/" + to.params.project_name + "/" + to.params.survey_name, params: to.params });
          }
        })
      })
    })
  } else {
    next();
  }
};

const formatPath = path => {
  return path.replace(/_/g, " ")
}

const toUpperCase = name => {
  return name.toUpperCase()
}

const isLocked = to => {
  const splitPath = to.path.split("/");

  const program = store.state.programs.programs.find(program => toUpperCase(program.name) === toUpperCase(formatPath(splitPath[1])))

  const project = store.state.projects.projects.find(project => toUpperCase(project.name) === toUpperCase(formatPath(splitPath[2])) && project.program === program.pk);

  const survey = store.state.survey.surveys.find(survey => toUpperCase(survey.name) === toUpperCase(formatPath(splitPath[3])) && survey.project === project.pk);

  return survey.locked
}

let program;
let project;
let survey;

const invalidProgram = name => {
  program = store.state.programs.programs.find(program => toUpperCase(program.name) === toUpperCase(formatPath(name)))
  if (!program) {
    return false;
  }
  return true
}

const invalidProject = (name, program) => {
  project = store.state.projects.projects.find(project => toUpperCase(project.name) === toUpperCase(formatPath(name)) && project.program === program.pk);
  if (!project) {
    return false
  }
  return true
}


const invalidSurvey = (name, project) => {
  survey = store.state.survey.surveys.find(survey => toUpperCase(survey.name) === toUpperCase(formatPath(name)) && survey.project === project.pk);
  if (!survey) {
    return false;
  }
  return true
}


const isValid = to => {
  const splitPath = to.path.split("/");
  if ( splitPath.length <= 3 || ( !splitPath[1] || !splitPath[2] || !splitPath[3] ) ) {
    return false;
  }

  if (!invalidProgram(splitPath[1])){
    return false;
  }
  if (!invalidProject(splitPath[2], program)){
    return false;
  }
  if (!invalidSurvey(splitPath[3], project)){
    return false;
  }

  return true;
}

const isAllowedToViewMultipleSurveySelection = (viewType) => {
  if (viewType === 'single') {
    return program.view_multiple_surveys && survey.view_in_list
  }
  return program.view_multiple_surveys
}

const validateTakingSingleSurveyURL = async (to, from, next) => {
  store.dispatch("programs/getPrograms").then(() => {
    store.dispatch("projects/getProjects").then(() => {
      store.dispatch("survey/fetchSurveys").then(() => {
        if (!isValid(to)) {
          next({name: "Error", params: { error: "InvalidSurvey"}});
        } else if (isLocked(to)) {
          next({name: "Error", params: { error: "LockedSurvey"}})
        } else if (isAllowedToViewMultipleSurveySelection('single')) {
          next({name: "ViewMultipleSurveys", params: { program_name: to.params.program_name }})
        } else {
          next()
        }
      })
    })
  })
};

const getSurveysBeforeEnter = (to, from, next) => {
  store.dispatch("survey/fetchSurveys").then(() => {
    next()
  });
}

const validateViewMultipleSurveys = async (to, from, next) => {
  store.dispatch("programs/getPrograms").then(() => {
    store.dispatch("projects/getProjects").then(() => {
      const splitPath = to.path.split("/");
      if (invalidProgram(splitPath[1])) {
        if (isAllowedToViewMultipleSurveySelection('multi')) {
          next();
        } else {
          next({name: "Error", params: { error: "NoViewMultipleSurveySelection"}});
        }
      } else {
        next({name: "Error", params: { error: "InvalidProgram"}});
      }
    })
  })
};

const isIE = (to, from, next) => {
  if (!window.document.documentMode) {
    next()
  } else {
    next({name: "Error", params: { error: "IE"}})
  }
}

const evaluateGuards = (guards, to, from, next) => {
  const guardsLeft = [...guards]; // clone array to avoid accidental modification
  const nextGuard = guardsLeft.shift();

  if (nextGuard === undefined){
    next();
    return;
  }

  nextGuard(to, from, (nextArg) => {
    if (nextArg === undefined) {
      evaluateGuards(guardsLeft, to, from, next);
      return;
    }
    next(nextArg);
  });
}

const multiGuard = guards => {
  if (!Array.isArray(guards)) {
    throw new Error('Guards argument must be an array');
  }

  return (to, from, next) => {
    return evaluateGuards(guards, to, from, next);
  }
}

const hasVirtualMeeting = async(to, from, next) => {
  store.dispatch("programs/getPrograms").then(() => {
    store.dispatch("projects/getProjects").then(() => {
      store.dispatch("survey/fetchSurveys").then(() => {
        if (isValid(to)) {
          if (survey.story_map_url) {
            next();
          } else {
            alert("There is no virtual meeting associated with this survey")
            next({ path: to.params.program_name + "/" + to.params.project_name + "/" + to.params.survey_name, params: to.params });
          }
        } else {
          next({name: "Error", params: { error: "InvalidSurvey"}});
        }
      })
    })
  })
}

export default new Router({
  routes:
  [
    {
      path: "/login",
      name: "login",
      component: Login,
      beforeEnter: requireUnauthenticated
    },
    {
      path: "/password/reset/",
      name: "passwordReset",
      component: PasswordReset
    },
    {
      path: "/password/reset/(.*)/(.*)",
      name: "passwordResetConfirm",
      component: PasswordResetConfirm
    },
    {
      path: "/email/verification/(.*)/",
      name: "emailVerification",
      component: EmailVerification
    },
    {
      path: "/admin",
      name: "admin",
      component: AdminMainContainer,
      beforeEnter: multiGuard([requireAuthenticated, isIE]),
      children: [
        {
          path: "/admin/dashboard",
          name: "dashboard",
          component: Dashboard,
          beforeEnter: multiGuard([requireAuthenticated, isIE]),
          children: [
            {
              path: "charts",
              component: Charts,
              beforeEnter: multiGuard([requireAuthenticated, isIE]),
            },
            {
              path: "stakeholderlist",
              component: StakeholderList,
              beforeEnter: multiGuard([requireAuthenticated, isIE]),
            },
            {
              path: "surveyresponses",
              component: SurveyResponses,
              beforeEnter: multiGuard([requireAuthenticated, isIE]),
            },
            {
              path: "map",
              component: Map,
              beforeEnter: multiGuard([requireAuthenticated, isIE]),
            },
            {
              path: "calendar",
              component: Calendar,
              beforeEnter: multiGuard([requireAuthenticated, isIE]),
            },
            {
              path: "exhibits",
              component: Exhibits,
              beforeEnter: multiGuard([requireAuthenticated, isIE]),
            }
          ],
        },
        {
          path: "/admin/surveys",
          name: "adminSurveys",
          component: AdminSurveys,
          beforeEnter: multiGuard([requireAuthenticated, isIE]),
        },
        {
          path: "/admin/surveys/:program_name/:project_name/:survey_name/preview",
          name: "previewSurvey",
          component: PreviewSurveys,
          beforeEnter: multiGuard([requireAuthenticated, isIE, getSurveysBeforeEnter])
        },
        {
          path: "/admin/conversations", // a path is required but this path should never actually be used
          name: "conversation",
          component: Conversation,
          props: true,
          beforeEnter: multiGuard([requireAuthenticated, isIE]),
          children: [
            {
              path: "/conversation/",
              name: "messageroom",
              component: MessageRoom,
              props: true
            }
          ]
        },
      ]
    },
    {
      path: "/:program_name/",
      name: "ViewMultipleSurveys",
      component: Home,
      beforeEnter: validateViewMultipleSurveys
    },
    {
      path: "/:program_name/:project_name/:survey_name/",
      name: "ViewSingleSurvey",
      component: Home,
      beforeEnter: validateTakingSingleSurveyURL
    },
    {
      path: "/:program_name/:project_name/:survey_name/virtual",
      name: "VirtualMeeting",
      component: StoryMap,
      beforeEnter: hasVirtualMeeting
    },
    {
      path: "/:program_name/:project_name/:survey_name/in-progress/",
      name: "survey",
      component: SurveyContainer,
      beforeEnter: requireLoggedIn
    },
    {
      path: "*",
      name: "Error",
      component: Error,
      props: true
    }
  ],
  mode: "history"
});
